import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { fileUrl, STORE_LABEL, dealTitle } from "../lib/api";
import Countdown from "./Countdown";

/**
 * Auto-rotating hero showcase. Cycles through up to N deals every 6 seconds,
 * pauses on hover, with dots indicators.
 */
export default function HeroCarousel({ deals = [] }) {
  const slides = (deals || []).slice(0, 5);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [paused, slides.length]);

  if (slides.length === 0) {
    return <div className="aspect-[4/5] rounded-3xl shimmer" data-testid="hero-empty" />;
  }

  const deal = slides[idx];

  return (
    <div
      data-testid="hero-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative"
    >
      <Link
        to={`/offerta/${deal.id}`}
        data-testid="hero-deal-card"
        className="relative block rounded-3xl overflow-hidden border border-[#1f2c4a] bg-[#131e3a] group"
      >
        <div className="relative aspect-[4/5] brick-pattern bg-[#0d162d] overflow-hidden">
          {deal.image_url ? (
            <img
              key={deal.id}
              src={fileUrl(deal.image_url)}
              alt={dealTitle(deal)}
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-all duration-1000 fade-up"
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-[#07070b] via-[#07070b]/30 to-transparent" />

          {deal.discount_percent ? (
            <div className="absolute top-5 right-5 bg-[#E11D27] text-white text-base font-black px-3 py-1.5 rounded-lg shadow-xl">
              -{deal.discount_percent}%
            </div>
          ) : null}

          <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 bg-[#FBD51C] text-black text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1.5 rounded-md">
            <Sparkles size={12} /> Offerta del giorno
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-6 fade-up" key={`info-${deal.id}`}>
            <div className="font-mono-bdd text-[10px] uppercase tracking-[0.25em] text-[#b6c1d6] mb-2 flex items-center gap-3">
              <span>{STORE_LABEL[deal.store] || "Store"}</span>
              {deal.expires_at && <Countdown to={deal.expires_at} compact />}
            </div>
            <div className="font-display font-black text-2xl lg:text-3xl text-white leading-tight tracking-tight line-clamp-2">
              {dealTitle(deal)}
            </div>
            <div className="flex items-end gap-3 mt-4">
              {deal.price_current ? (
                <div className="font-display font-black text-3xl text-white">
                  €{deal.price_current.toFixed(2)}
                </div>
              ) : null}
              {deal.price_original && deal.price_original > (deal.price_current || 0) ? (
                <div className="text-[#6e7896] line-through pb-1">
                  €{deal.price_original.toFixed(2)}
                </div>
              ) : null}
              <div className="ml-auto inline-flex items-center gap-1 text-[#FBD51C] text-sm font-bold pb-1">
                Vedi offerta <ArrowRight size={14} />
              </div>
            </div>
          </div>
        </div>
      </Link>

      {slides.length > 1 && (
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
          {slides.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-dot-${i}`}
              aria-label={`Slide ${i + 1}`}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "bg-[#FBD51C] w-8" : "bg-[#1f2c4a] hover:bg-[#2a3a63] w-3"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
